import {
  Monitor,
  Play,
  Shield,
  Flag,
  Grid,
  Camera,
  Server,
  Smartphone,
  RotateCcw,
  Lock,
  MessageCircle,
  Gift,
  Diamond,
  CheckCircle,
  Trophy,
  Tv,
  Zap,
} from "lucide-react";
import { useState } from "react";
import apple from '../assets/logo/Apple_Pay.png';
import discover from '../assets/logo/Discover.png';
import cb from '../assets/logo/GIE_CB.png';
import Google from '../assets/logo/Google_Pay.png';
import mastercard from '../assets/logo/Mastercard.png';
import visa from '../assets/logo/Visa.png';
import ideal from '../assets/logo/ideal.svg';
import bancontact from '../assets/logo/Bancontact.png';
import eredivisie from "../assets/cover/sports/eredivisie.jpg";
import championsLeague from "../assets/cover/sports/champions-league.png";
import { CONTACT_CONFIG } from "../config/contact";
import SectionHeading from "../components/SectionHeading";
import Reveal from "../components/Reveal";
import GridTexture from "../components/GridTexture";

const filmContext = require.context("../assets/cover/film", false, /\.(png|jpe?g|svg|webp)$/);
const filmImages = filmContext.keys().map((key) => filmContext(key));
const lifetimePosters = [
  { src: eredivisie, mode: "cover" },
  { src: filmImages[0], mode: "cover" },
  { src: championsLeague, mode: "cover" },
  { src: filmImages[1], mode: "cover" },
].filter((p) => Boolean(p.src));

function getDiscountPercent(original, current) {
  return Math.round(((original - current) / original) * 100);
}

const FEATURE_TONES = {
  cyan: "text-[#00d4ff]",
  blue: "text-[#3b82f6]",
  purple: "text-[#8b5cf6]",
  violet: "text-[#a855f7]",
  pink: "text-[#ec4899]",
};

function FeatureRow({ icon: Icon, tone = "blue", children }) {
  const toneClass = FEATURE_TONES[tone] || FEATURE_TONES.blue;
  return (
    <div className="flex items-start gap-2 sm:gap-2.5">
      <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${toneClass}`} />
      <span className="text-ink-secondary text-xs sm:text-sm leading-snug">{children}</span>
    </div>
  );
}

function AdultRow() {
  return (
    <div className="flex items-start gap-2 sm:gap-2.5">
      <div className="relative flex-shrink-0">
        <Lock className="w-4 h-4 mt-0.5 text-pink-300" />
        <div className="absolute -top-2 -right-2 bg-pink-500 text-white text-[9px] rounded-full w-4 h-4 flex items-center justify-center font-bold">
          18
        </div>
      </div>
      <span className="text-ink-secondary text-xs sm:text-sm leading-snug">
        Volwassenenzenders + films (met wachtwoord) 🔞
      </span>
    </div>
  );
}

/** Full feature list — same content on every card, just like the packages page this replaced. */
function PlanFeatures({ adult = false }) {
  return (
    <>
      <FeatureRow icon={Diamond} tone="cyan">
        <strong className="text-accent">Officiële TiviOne-server</strong> voor NL &amp; België
      </FeatureRow>
      <FeatureRow icon={Trophy} tone="pink">
        <strong className="text-[#ec4899]">Eredivisie &amp; Champions League</strong> 2026/27 in HD/4K
      </FeatureRow>
      {adult && <AdultRow />}
      <FeatureRow icon={Play} tone="violet">
        Netflix, Disney+, Amazon Prime, HBO Max, Videoland, Viaplay, NPO Start Plus, Discovery+
      </FeatureRow>
      <FeatureRow icon={Shield} tone="blue">
        ESPN Nederland voor de Eredivisie ⚽️
      </FeatureRow>
      <FeatureRow icon={Trophy} tone="purple">
        Ziggo Sport &amp; Viaplay voor Formule 1
      </FeatureRow>
      <FeatureRow icon={Flag} tone="pink">
        Onbeperkte keuze aan films en series
      </FeatureRow>
      <FeatureRow icon={Grid} tone="purple">
        Kwaliteit SD / HD / FULL HD / 4K
      </FeatureRow>
      <FeatureRow icon={Camera} tone="cyan">
        Catch-up &amp; EPG (tv-gids)
      </FeatureRow>
      <FeatureRow icon={Smartphone} tone="violet">
        Compatibel met alle apparaten
      </FeatureRow>
      <FeatureRow icon={RotateCcw} tone="violet">
        Regelmatig bijgewerkte content
      </FeatureRow>
      <FeatureRow icon={Shield} tone="purple">
        15 dagen geld-terug-garantie
      </FeatureRow>
    </>
  );
}

const TOP_FEATURES = [
  {
    icon: Tv,
    tone: "cyan",
    title: "79.000+ kanalen van over de hele wereld",
    text: "Een volledige lijst van alle beschikbare Europese zenders. Met meer dan 79.000 tv-kanalen bieden we de meest geavanceerde zenderlijst op de markt.",
  },
  {
    icon: Zap,
    tone: "purple",
    title: "Ultrasnelle verbinding",
    text: "TiviOne gebruikt een hoogwaardige Europese dedicated server. Het openen van onze tv-kanalen duurt minder dan 0,5 seconde.",
  },
  {
    icon: Gift,
    tone: "pink",
    title: "Onmiddellijke installatie",
    text: "Configureer je IPTV-abonnement eenvoudig op elk apparaat. Wij bieden gratis installatie op afstand en activering van de IBO Pro Player, zonder verborgen kosten.",
  },
];

function TopFeatureCard({ icon: Icon, tone, title, text }) {
  return (
    <div className="relative rounded-2xl sm:rounded-3xl bg-gradient-button p-[3px] h-full shadow-glow-cyan-sm">
      <div className="rounded-[calc(1rem-3px)] sm:rounded-[calc(1.5rem-3px)] bg-surface-card p-5 sm:p-6 h-full">
        <Icon className={`w-6 h-6 mb-3 ${FEATURE_TONES[tone] || FEATURE_TONES.cyan}`} />
        <h3 className="font-bold text-white text-sm sm:text-base mb-1.5">{title}</h3>
        <p className="text-ink-secondary text-xs sm:text-sm leading-relaxed">{text}</p>
      </div>
    </div>
  );
}

function TopFeatures() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5">
      {TOP_FEATURES.map((f) => (
        <TopFeatureCard key={f.title} {...f} />
      ))}
    </div>
  );
}

function IboBanner() {
  return (
    <div className="mb-8 sm:mb-10 rounded-xl sm:rounded-2xl bg-gradient-button p-[3px] shadow-glow-purple-sm">
      <div className="rounded-[calc(0.75rem-3px)] sm:rounded-[calc(1rem-3px)] bg-surface-card px-5 py-4 sm:px-8 sm:py-5 flex items-center justify-center gap-2.5 sm:gap-3 text-center">
        <Gift className="w-5 h-5 text-white flex-shrink-0" />
        <span className="text-white font-bold text-sm sm:text-base">
          Gratis Activering Voor De IBO Pro Player-app 🎁
        </span>
      </div>
    </div>
  );
}

const PAYMENT_METHODS = [
  { src: ideal, alt: "iDEAL", h: "h-8 sm:h-9" },
  { src: bancontact, alt: "Bancontact", h: "h-5 sm:h-6" },
  { src: Google, alt: "Google Pay", h: "h-5 sm:h-6" },
  { src: cb, alt: "CB", h: "h-7 sm:h-8" },
  { src: visa, alt: "Visa", h: "h-4 sm:h-5" },
  { src: apple, alt: "Apple Pay", h: "h-5 sm:h-6" },
  { src: discover, alt: "Discover", h: "h-3.5 sm:h-4" },
  { src: mastercard, alt: "Mastercard", h: "h-7 sm:h-8" },
];

function PaymentBanner() {
  return (
    <div className="mt-10 sm:mt-14 max-w-4xl mx-auto">
      <div className="bg-surface-card border border-line rounded-xl sm:rounded-2xl shadow-lg p-6 sm:p-8 text-center">
        <div className="inline-flex items-center gap-2 text-accent font-bold text-sm sm:text-base">
          <Lock className="w-4 h-4 sm:w-5 sm:h-5" />
          100% Veilig &amp; versleuteld betalen
        </div>
        <p className="text-xs sm:text-sm text-ink-muted mt-1 mb-5 sm:mb-6">
          Betaal eenvoudig met iDEAL, Bancontact of je favoriete kaart
        </p>
        <div className="grid grid-cols-4 md:grid-cols-8 gap-2.5 sm:gap-3">
          {PAYMENT_METHODS.map((p, i) => (
            <div
              key={p.alt}
              style={{ animationDelay: `${i * 80}ms` }}
              className="pay-tile group bg-white border border-white/10 rounded-xl h-14 sm:h-16 flex items-center justify-center transition-all duration-300 hover:-translate-y-1 hover:shadow-md hover:border-accent/40"
            >
              <img
                src={p.src}
                alt={p.alt}
                className={`${p.h} w-auto max-w-[78%] object-contain drop-shadow-sm transition-transform duration-300 group-hover:scale-110`}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function formatPrice(price) {
  return `€${price}`;
}

/** Corner ribbon badge — flat diagonal banner instead of a floating pill. */
function RibbonBadge({ label }) {
  if (!label) return null;
  return (
    <div className="absolute top-0 right-0 w-28 h-28 overflow-hidden rounded-tr-2xl sm:rounded-tr-3xl pointer-events-none">
      <div className="absolute top-[16px] right-[-38px] w-[150px] rotate-45 bg-gradient-button text-white text-[9px] sm:text-[10px] font-bold uppercase tracking-wide text-center py-1 shadow-md">
        {label}
      </div>
    </div>
  );
}

/** Simple, flat pricing card — full feature list, wrapped in the site's gradient frame so it stands out on black. */
function PricingCard({ plan, isPremium, badgeLabel, adult, onOrder }) {
  return (
    <div
      className={`relative rounded-2xl sm:rounded-3xl bg-gradient-button p-[3px] h-full ${
        isPremium ? "shadow-glow-purple-sm" : "shadow-glow-cyan-sm"
      }`}
    >
      <div className="relative overflow-hidden rounded-[calc(1rem-3px)] sm:rounded-[calc(1.5rem-3px)] bg-surface-card p-6 sm:p-8 flex flex-col h-full">
        <RibbonBadge label={badgeLabel} />

        <h3 className="text-xl font-black text-white mb-2">{plan.name}</h3>
        {plan.badge && (
          <span className="inline-block w-fit bg-accent-soft border border-accent/30 text-accent px-3 py-1 rounded-full text-[11px] sm:text-xs font-bold mb-4 sm:mb-5">
            {plan.badge}
          </span>
        )}

        <div className="mb-5 sm:mb-6">
          {plan.originalPrice && (
            <div className="flex items-center gap-2">
              <span className="line-through text-sm text-ink-muted">{formatPrice(plan.originalPrice)}</span>
              <span className="text-[11px] font-bold text-[#ec4899]">
                -{getDiscountPercent(plan.originalPrice, plan.currentPrice)}%
              </span>
            </div>
          )}
          <div className="flex items-baseline gap-2">
            <span className="text-3xl sm:text-4xl font-black text-white">€{plan.currentPrice}</span>
            {plan.durationLabel && (
              <span className="text-xs sm:text-sm text-white/90 font-medium">{plan.durationLabel}</span>
            )}
          </div>
        </div>

        <div className="space-y-2.5 sm:space-y-3 mb-6 sm:mb-8 flex-1">
          <PlanFeatures adult={adult} />
        </div>

        <button
          className="w-full font-bold py-3 sm:py-3.5 px-4 sm:px-6 rounded-xl transition-colors duration-300 text-sm sm:text-base min-h-[48px] bg-gradient-button hover:brightness-110 text-white"
          onClick={onOrder}
        >
          NU BESTELLEN
        </button>
      </div>
    </div>
  );
}

const LIFETIME_HIGHLIGHTS = [
  { icon: Diamond, label: "Officiële TiviOne-server voor NL & België" },
  { icon: Trophy, label: "Eredivisie & Champions League 2026/27 in HD/4K" },
  { icon: CheckCircle, label: "Eenmalige betaling — geen abonnement" },
  { icon: Monitor, label: "2 gelijktijdige schermen" },
];

/** Lifetime plan — a compact wide banner with a small NL sport/film poster collage on the right. */
function LifetimeBanner({ data, posters, onOrder }) {
  return (
    <div className="mt-6 sm:mt-8">
      <div className="relative overflow-hidden rounded-xl sm:rounded-2xl bg-gradient-button p-[3px] shadow-glow-purple-sm">
        <div className="relative overflow-hidden rounded-[calc(0.75rem-3px)] sm:rounded-[calc(1rem-3px)] bg-surface-card">
          <RibbonBadge label="LIFETIME" />
          <div className="relative grid md:grid-cols-[1.3fr_1fr] gap-5 sm:gap-6 items-center p-4 sm:p-6">
            <div className="text-center md:text-left">
              <span className="inline-block w-fit bg-white/10 border border-accent/30 text-accent px-2.5 py-0.5 rounded-full text-[10px] sm:text-[11px] font-bold mb-2 sm:mb-3">
                Eenmalige betaling
              </span>
              <h3 className="text-lg sm:text-xl font-black text-white mb-2">{data.name}</h3>

              <div className="flex items-center gap-2 justify-center md:justify-start">
                <span className="line-through text-xs sm:text-sm text-ink-muted">
                  {formatPrice(data.originalPrice)}
                </span>
                <span className="text-[10px] sm:text-[11px] font-bold text-[#ec4899]">
                  -{getDiscountPercent(data.originalPrice, data.price)}%
                </span>
              </div>
              <div className="flex items-baseline gap-2 justify-center md:justify-start mb-3 sm:mb-4">
                <span className="text-2xl sm:text-3xl font-black text-white">€{data.price}</span>
                <span className="text-xs sm:text-sm text-white/90 font-medium">/ eenmalig</span>
              </div>

              <div className="grid grid-cols-2 gap-2 sm:gap-2.5 mb-4 sm:mb-5 text-left">
                {LIFETIME_HIGHLIGHTS.map((f) => (
                  <div key={f.label} className="flex items-start gap-1.5 sm:gap-2">
                    <f.icon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-accent" />
                    <span className="text-ink-secondary text-[11px] sm:text-xs leading-snug">{f.label}</span>
                  </div>
                ))}
              </div>

              <button
                className="w-full md:w-auto bg-gradient-button hover:brightness-110 text-white font-black py-2.5 sm:py-3 px-6 rounded-lg sm:rounded-xl shadow-glow-purple-sm hover:shadow-lg transition-all duration-300 inline-flex items-center justify-center gap-2 text-sm sm:text-base"
                onClick={onOrder}
              >
                <Gift className="w-4 h-4" />
                NU BESTELLEN
              </button>
            </div>

            {posters.length > 0 && (
              <div className="flex items-center justify-center -space-x-6 sm:-space-x-9 lg:-space-x-12">
                {posters.map((p, i) => (
                  <div
                    key={i}
                    className={`w-24 h-32 sm:w-32 sm:h-44 lg:w-40 lg:h-52 rounded-lg shadow-xl border-2 border-white/10 transition-transform duration-500 hover:scale-105 hover:z-20 overflow-hidden ${
                      i % 2 === 0 ? "rotate-[-6deg]" : "rotate-[6deg]"
                    } ${p.mode === "contain" ? "bg-white flex items-center justify-center p-2" : ""}`}
                    style={{ zIndex: i === 1 || i === 2 ? 10 : 1 }}
                  >
                    <img
                      src={p.src}
                      alt=""
                      aria-hidden="true"
                      loading="lazy"
                      className={p.mode === "contain" ? "max-w-full max-h-full object-contain" : "w-full h-full object-cover"}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Pricing() {
  const { whatsappUrl: WHATSAPP_URL } = CONTACT_CONFIG;
  const [isMultiScreen, setIsMultiScreen] = useState(false);

  const PRICING_DATA = {
    basic: {
      name: "BASISPAKKET",
      originalPrice: 59.0,
      currentPrice: 49.0,
      duration: "12 MAANDEN",
      bonus: "12 MAANDEN",
      link: "https://tivi-one1.shop/order.php?id=1",
    },
    premium: {
      name: "PREMIUMPAKKET",
      originalPrice: 69.0,
      currentPrice: 59.0,
      duration: "15 MAANDEN",
      bonus: "15 MAANDEN",
      link: "https://tivi-one1.shop/order.php?id=2",
    },
    gold: {
      name: "GOUDEN PAKKET",
      originalPrice: 89.0,
      currentPrice: 79.0,
      duration: "15 MAANDEN",
      bonus: "2 SCHERMEN",
      link: "https://tivi-one1.shop/order.php?id=3",
    },
    multi_2_screens: {
      name: "DIAMANTEN PAKKET",
      originalPrice: 99.0,
      currentPrice: 89.0,
      duration: "2 jaar",
      bonus: "2 SCHERMEN",
      link: "https://tivi-one1.shop/order.php?id=4",
    },
    multi_3_screens: {
      name: "PLATINUMPAKKET",
      originalPrice: 119.0,
      currentPrice: 99.0,
      duration: "15 MAANDEN",
      bonus: "3 SCHERMEN",
      link: "https://tivi-one1.shop/order.php?id=5",
    },
    multi_4_screens: {
      name: "LUXEPAKKET",
      originalPrice: 129.0,
      currentPrice: 109.0,
      duration: "15 MAANDEN",
      bonus: "4 SCHERMEN",
      link: "https://tivi-one1.shop/order.php?id=6",
    },
    lifetime: {
      name: "LIFETIME-PAKKET",
      originalPrice: 299.0,
      price: 210.0,
      link: "https://tivi-one1.shop/order.php?id=7",
    },
  };

  const standardPlans = [
    {
      id: "standard-basic",
      name: PRICING_DATA.basic.name,
      badge: PRICING_DATA.basic.bonus,
      originalPrice: PRICING_DATA.basic.originalPrice,
      currentPrice: PRICING_DATA.basic.currentPrice,
      durationLabel: `/${PRICING_DATA.basic.duration}`,
      checkoutPlan: "basic",
      redirectUrl: PRICING_DATA.basic.link,
      badgeLabel: null,
      adult: false,
    },
    {
      id: "standard-premium",
      name: PRICING_DATA.premium.name,
      badge: PRICING_DATA.premium.bonus,
      originalPrice: PRICING_DATA.premium.originalPrice,
      currentPrice: PRICING_DATA.premium.currentPrice,
      durationLabel: `/${PRICING_DATA.premium.duration}`,
      checkoutPlan: "premium",
      redirectUrl: PRICING_DATA.premium.link,
      badgeLabel: "MEEST POPULAIR",
      adult: true,
      premium: true,
    },
    {
      id: "standard-gold",
      name: PRICING_DATA.gold.name,
      badge: PRICING_DATA.gold.bonus,
      originalPrice: PRICING_DATA.gold.originalPrice,
      currentPrice: PRICING_DATA.gold.currentPrice,
      durationLabel: `/${PRICING_DATA.gold.duration}`,
      checkoutPlan: "gold",
      redirectUrl: PRICING_DATA.gold.link,
      badgeLabel: "BESTE PRIJS",
      adult: true,
    },
  ];

  const multiScreenPlans = [
    {
      id: "DIAMANTEN PAKKET",
      name: PRICING_DATA.multi_2_screens.name,
      badge: `${PRICING_DATA.multi_2_screens.bonus} • ${PRICING_DATA.multi_2_screens.duration.toUpperCase()}`,
      originalPrice: PRICING_DATA.multi_2_screens.originalPrice,
      currentPrice: PRICING_DATA.multi_2_screens.currentPrice,
      durationLabel: `/${PRICING_DATA.multi_2_screens.duration}`,
      checkoutPlan: "multi_2_screens",
      redirectUrl: PRICING_DATA.multi_2_screens.link,
      badgeLabel: null,
      adult: false,
    },
    {
      id: "PLATINUMPAKKET",
      name: PRICING_DATA.multi_3_screens.name,
      badge: `${PRICING_DATA.multi_3_screens.bonus} • ${PRICING_DATA.multi_3_screens.duration.toUpperCase()}`,
      originalPrice: PRICING_DATA.multi_3_screens.originalPrice,
      currentPrice: PRICING_DATA.multi_3_screens.currentPrice,
      durationLabel: `/${PRICING_DATA.multi_3_screens.duration}`,
      checkoutPlan: "multi_3_screens",
      redirectUrl: PRICING_DATA.multi_3_screens.link,
      badgeLabel: "MEEST POPULAIR",
      adult: true,
      premium: true,
    },
    {
      id: "LUXEPAKKET",
      name: PRICING_DATA.multi_4_screens.name,
      badge: `${PRICING_DATA.multi_4_screens.bonus} • ${PRICING_DATA.multi_4_screens.duration.toUpperCase()}`,
      originalPrice: PRICING_DATA.multi_4_screens.originalPrice,
      currentPrice: PRICING_DATA.multi_4_screens.currentPrice,
      durationLabel: `/${PRICING_DATA.multi_4_screens.duration}`,
      checkoutPlan: "multi_4_screens",
      redirectUrl: PRICING_DATA.multi_4_screens.link,
      badgeLabel: "BESTE PRIJS",
      adult: true,
    },
  ];

  const activePlans = isMultiScreen ? multiScreenPlans : standardPlans;

  const createCheckoutUrl = (plan, redirectUrl) => {
    return `/checkout?plan=${encodeURIComponent(plan)}&redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <>
      <section
        className="py-14 sm:py-20 bg-surface-secondary animated-bg"
        style={{ "--section-bg": "#000000" }}
      >
        <GridTexture />
        <div className="main-content w-[90%] sm:w-[90%] md:w-[85%] m-auto relative">
          <Reveal className="flex justify-center mb-5 sm:mb-6">
            <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white/5 border border-accent/25 shadow-glow-cyan-sm">
              <Server className="w-4 h-4 sm:w-5 sm:h-5 text-accent flex-shrink-0" />
              <span className="text-sm sm:text-base font-bold uppercase tracking-[0.22em] text-transparent bg-clip-text bg-gradient-text">
                Officiële Server · TiviOne
              </span>
            </div>
          </Reveal>

          <TopFeatures />

          <div className="mt-10 sm:mt-14">
            <SectionHeading
              kicker="Abonnementen"
              title={
                <>
                  Ontdek Onze{" "}
                  <span className="text-transparent bg-clip-text bg-gradient-text">
                    Beste Pakketten
                  </span>
                </>
              }
              subtitle="Speciale Seizoensaanbieding 2026/27 — tijdelijk verlaagde prijzen"
            />
          </div>

          <IboBanner />

          {/* Toggle Standard / Multi-Screen */}
          <div className="flex justify-center mb-8 sm:mb-10">
            <div className="inline-flex items-center rounded-full bg-surface-card shadow-md p-1.5 border border-line">
              <button
                type="button"
                onClick={() => setIsMultiScreen(false)}
                className={`px-6 sm:px-9 py-2.5 sm:py-3.5 text-sm sm:text-base font-bold rounded-full transition-all duration-300 ${
                  !isMultiScreen
                    ? "bg-gradient-button text-white shadow-glow-cyan-sm"
                    : "text-ink-secondary hover:text-white"
                }`}
              >
                STANDAARD
              </button>
              <button
                type="button"
                onClick={() => setIsMultiScreen(true)}
                className={`px-6 sm:px-9 py-2.5 sm:py-3.5 text-sm sm:text-base font-bold rounded-full transition-all duration-300 ${
                  isMultiScreen
                    ? "bg-gradient-button text-white shadow-glow-cyan-sm"
                    : "text-ink-secondary hover:text-white"
                }`}
              >
                MULTI-SCHERM
              </button>
            </div>
          </div>

          {/* Pricing Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8" id="pricing">
            {activePlans.map((plan) => (
              <PricingCard
                key={plan.id}
                plan={plan}
                isPremium={!!plan.premium}
                badgeLabel={plan.badgeLabel}
                adult={plan.adult}
                onOrder={() =>
                  window.open(
                    createCheckoutUrl(plan.checkoutPlan, plan.redirectUrl),
                    "_blank"
                  )
                }
              />
            ))}
          </div>

          {/* LIFETIME PLAN — compact banner with an Eredivisie + NL film/series poster collage */}
          <LifetimeBanner
            data={PRICING_DATA.lifetime}
            posters={lifetimePosters}
            onOrder={() =>
              window.open(createCheckoutUrl("lifetime", PRICING_DATA.lifetime.link), "_blank")
            }
          />

          {/* Betaalmethoden-banner */}
          <PaymentBanner />

          {/* Contact Section */}
          <div className="mt-12 sm:mt-16 text-center">
            <div className="bg-surface-card border border-line rounded-xl sm:rounded-2xl p-6 sm:p-8 shadow-lg max-w-4xl mx-auto">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4 sm:gap-6">
                <div className="text-center md:text-left">
                  <p className="text-base sm:text-lg text-ink-secondary leading-relaxed">
                    <span className="text-accent font-semibold">
                      Wilt u een abonnement op maat
                    </span>
                    <span className="text-white font-semibold"> (land, taal, schermen),</span>
                    <br />
                    <span className="text-white font-semibold">
                      neem dan contact met ons op via WhatsApp of e-mail.
                    </span>
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <a
                    href={WHATSAPP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 sm:gap-3 bg-[#25d366] hover:bg-[#128C7E] text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg sm:rounded-xl font-semibold transition-all duration-300 hover:shadow-lg hover:scale-105 text-sm sm:text-base min-h-[44px]"
                  >
                    <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                    WhatsApp
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
